// src/constants/menu.ts

export type Menu = {
  /** Top-level menu group label shown in the sidebar */
  menu: string;
  /** Sub menu items (each renders as a clickable navigation link) */
  subMenu: {
    /** Display name of the submenu item */
    name: string;
    /** Route path for this submenu item */
    path: string;
    /**
     * When true, this submenu is active only when location.pathname === path.
     * When omitted/false, it's also active for nested routes like `/path/...`.
     */
    exact?: boolean;
  }[];
};

/**
 * Admin-side menu definition
 *
 * Target users:
 *  - ADMINISTRATOR
 *  - MANAGER
 *  - PRODUCT_MANAGER
 *
 * Notes:
 *  - Top-level `menu` is used as group title in the sidebar.
 *  - `subMenu` entries correspond to actual navigation links.
 */
export const ADMIN_MENU_LIST: Menu[] = [
  {
    menu: 'Home',
    subMenu: [
      {
        // Dashboard-style home, but keep route path as /home
        name: 'Dashboard',
        path: '/home',
      },
    ],
  },
  {
    // Order management (all orders in the system)
    menu: 'Orders',
    subMenu: [
      {
        name: 'Order List',
        path: '/orders',
        // admin 쪽은 /orders/add 같은 별도 메뉴가 없어서 exact 안 줘도 됨
      },
    ],
  },
  {
    // Products sold to partners
    menu: 'Products',
    subMenu: [
      {
        name: 'Product List',
        path: '/products',
        // /products/upload 에서 Product List 까지 동시에 선택되는 문제 방지
        exact: true,
      },
      {
        // Bulk registration of products using Excel
        name: 'Product Bulk Upload',
        path: '/products/upload',
      },
    ],
  },
  {
    // Stock / SKU / eSIM inventory management
    menu: 'Stocks',
    subMenu: [
      {
        name: 'Stock Codes (SKU)',
        path: '/stocks/codes',
      },
      {
        name: 'eSIM Inventory',
        path: '/stocks/esim-inventory',
      },
      {
        // Bulk registration of eSIMs
        name: 'eSIM Bulk Upload',
        path: '/stocks/upload',
      },
      {
        // Bulk update of eSIM info (status / validity)
        name: 'eSIM Bulk Update',
        path: '/stocks/update',
      },
      {
        // Supplier (upstream provider) management
        name: 'Suppliers',
        path: '/stocks/suppliers',
      },
    ],
  },
  {
    // eSIMSTA partner (B2B client) management
    menu: 'Partners',
    subMenu: [
      {
        name: 'Partner List',
        path: '/partners',
        // /partners/add 에서 Partner List까지 같이 active 되는 것 방지
        exact: true,
      },
      {
        name: 'Add Partner',
        path: '/partners/add',
      },
    ],
  },
  {
    // Internal system user (our staff) management
    menu: 'Users',
    subMenu: [
      {
        name: 'User List',
        path: '/users',
      },
    ],
  },
  // Dev-only tools. Tree-shaken in production builds.
  ...(process.env.NODE_ENV === 'development'
    ? [
        {
          menu: 'Internal Tools',
          subMenu: [
            {
              name: 'API Runner',
              path: '/internal/api-runner',
            },
          ],
        },
      ]
    : []),
];

/**
 * Partner-side menu (standard)
 *
 * Target users:
 *  - PARTNER (manual order feature disabled)
 */
export const PARTNER_MENU_LIST: Menu[] = [
  {
    // Own partner account profile & settings
    menu: 'Partner Info',
    subMenu: [
      {
        name: 'Partner Info',
        path: '/my-detail',
      },
    ],
  },
  {
    // Orders placed by this partner
    menu: 'Orders',
    subMenu: [
      {
        name: 'Order List',
        path: '/orders',
      },
    ],
  },
  {
    // Products that this partner can sell
    menu: 'Products',
    subMenu: [
      {
        name: 'My Products',
        path: '/my-products',
        // /my-products/:id 에서도 My Products를 active로 두고 싶어서 exact 안 줌
      },
    ],
  },
];

/**
 * Partner-side menu (manual/direct order enabled)
 *
 * Target users:
 *  - PARTNER with `manualOrderEnabled === true`
 *
 * Difference from PARTNER_MENU_LIST:
 *  - Adds "Manual Order" entry under Orders.
 */
export const PARTNER_MENU_LIST_WITH_MANUAL_ORDER: Menu[] = [
  {
    menu: 'Partner Info',
    subMenu: [
      {
        name: 'Partner Info',
        path: '/my-detail',
      },
    ],
  },
  {
    menu: 'Orders',
    subMenu: [
      {
        name: 'Order List',
        path: '/orders',
        // /orders/add 에서 Order List까지 같이 active 되는 문제 방지
        exact: true,
      },
      {
        // Direct/manual order creation screen
        name: 'Manual Order',
        path: '/orders/add',
      },
    ],
  },
  {
    menu: 'Products',
    subMenu: [
      {
        name: 'My Products',
        path: '/my-products',
      },
    ],
  },
];
